import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { REACT_APP_GLOBAL_API_BASE_URL_V2 } from 'runtimeEnvVars';

import { addDefaultErrorMessageAction } from 'containers/FlashMessage/actions';
import { apiGet } from 'utils/axios';

interface Owner {
  id: string;
  name: string;
}

interface OwnersResponse {
  owners: Owner[];
  pagination: { next_page: number | null };
}

export interface OpeningOwnerOption {
  id: string;
  title: string;
}

/**
 * Pages through the distinct owners of the host's accessible openings from
 * `/openings/owners`. The endpoint pages at 50 and searches by name, so both
 * the search and the next page are server requests.
 */
export const useOpeningOwners = (userId?: string) => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [owners, setOwners] = useState<OpeningOwnerOption[]>([]);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  // A slower response for an older query must not overwrite a newer one.
  const seq = useRef(0);

  const fetchPage = useCallback(
    async (page: number) => {
      seq.current += 1;
      const id = seq.current;
      setIsFetching(true);
      try {
        const { data } = await apiGet(
          `${REACT_APP_GLOBAL_API_BASE_URL_V2}/openings/owners`,
          { user_id: userId, query, page },
        );
        if (id !== seq.current) return;

        const { owners: pageOwners, pagination } = data as OwnersResponse;
        const options = pageOwners.map(owner => ({
          id: owner.id,
          title: owner.name,
        }));
        setOwners(prev => (page === 1 ? options : [...prev, ...options]));
        setNextPage(pagination.next_page);
      } catch {
        if (id !== seq.current) return;
        dispatch(addDefaultErrorMessageAction());
      } finally {
        if (id === seq.current) setIsFetching(false);
      }
    },
    [userId, query, dispatch],
  );

  useEffect(() => {
    void fetchPage(1);
  }, [fetchPage]);

  const fetchNextPage = useCallback(() => {
    if (!isFetching && nextPage) void fetchPage(nextPage);
  }, [isFetching, nextPage, fetchPage]);

  return { owners, setQuery, fetchNextPage, isFetching };
};
